'use client';

import { useState } from 'react';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import smoothscroll from 'smoothscroll-polyfill';
import clsx from 'clsx';

import { TargetToTopButton } from '@/components/widgets/to-top-button';
import { useStreamingStore } from '@/store/streaming';
import { useParams, usePathname } from 'next/navigation';
import { Betslip } from '@/features/betslip/betslip';
import { useSiteStore } from '@/store/site';
import { useTargetScrollableContext } from './contexts/target-scrollable-context';

import { miniStreamingService } from '@/services/mini-streaming.service';
import { isNumberGames, isVirtualSports } from '@/services/settings.service';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { ThirdPartyTopMenu } from '@/features/top-menu/third-party-top-menu';

interface TargetScrollableContainerProps {
	className?: string;
	isSimplePage?: boolean;
	children: React.ReactNode;
}

export const TargetScrollableContainer = ({
	className,
	isSimplePage = false,
	children
}: TargetScrollableContainerProps) => {
	const scrollableRef = useTargetScrollableContext();
	const { matchId, sport } = useParams();
	const isSingleMatchPage = matchId !== undefined;
	const isNumberGame = isNumberGames(Number(sport));
	const isVirtualSport = isVirtualSports(Number(sport));
	const view = useAppearanceStore(state => state.view);
	const { scrollY: bodyScrollY } = useScroll();
	const { scrollY } = useScroll({ container: scrollableRef });
	const [isVisible, setIsVisible] = useState(false);
	const [isExpnadChatroom, streamingSrc, streamingPluginUrl, chatroomUrl] = useStreamingStore(
		state => [state.isExpnadChatroom, state.streamingSrc, state.streamingPluginUrl, state.chatroomUrl]
	);
	const hasIframeUrl = chatroomUrl !== '' || streamingPluginUrl !== '';
	const isOpenTournamentCountry = useSiteStore(state => state.isOpenTournamentCountry);
	const containerClassName = clsx(
		isSimplePage ? 'simple-page__scrollbar scrollbar' : 'main-wrap__content wrap-scrollbar',
		className
	);

	useMotionValueEvent(scrollY, 'change', (latest) => {
		setIsVisible(latest > 30)
	});

	useMotionValueEvent(bodyScrollY, 'change', (latest) => {
		setIsVisible(latest > 30)
		miniStreamingService.setIsScrolledTop(latest);
	})

	const scrollToTop = () => {
		smoothscroll.polyfill();
		scrollableRef.current?.scroll({
			top: 0,
			behavior: 'smooth'
		});
	};

	const bodyScrollToTop = () => {
		smoothscroll.polyfill();
		scroll({
			top: 0,
			behavior: 'smooth'
		});
	};
	return (
		<div
			ref={scrollableRef}
			className={`${containerClassName}`}
			data-schedule-menu-list-open={isOpenTournamentCountry}
			{...(hasIframeUrl ? { 'data-chatroom-open': isExpnadChatroom ? true : false } : {})}
		>
			{view === ViewEnum.galaxy && (isNumberGame || isVirtualSport) ? <ThirdPartyTopMenu sportId={Number(sport)} /> : null}
			{children}
			<TargetToTopButton
				onScrollToTop={scrollToTop}
				className="desktop"
				isVisible={isVisible}
			/>
			<TargetToTopButton
				onScrollToTop={bodyScrollToTop}
				className="mobile"
				isVisible={isVisible}
			/>
		</div>
	);
};
